import React, { useState, useEffect, useCallback } from 'react';
import { useDrag } from '@use-gesture/react';
import { animated, useSpring } from '@react-spring/web';

export default function Lightbox({ photos, startIndex, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [isMobile, setIsMobile] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);

  // Spring animation for smooth dragging
  const [{ y }, api] = useSpring(() => ({ y: 0 }));

  // Detect mobile viewport
  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    setIsMobile(mediaQuery.matches);

    const handler = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Hide swipe hint after first interaction
  useEffect(() => {
    if (isMobile && showSwipeHint) {
      const timer = setTimeout(() => setShowSwipeHint(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, showSwipeHint]);

  // Reset spring animation when index changes
  useEffect(() => {
    api.start({ y: 0, immediate: true });
  }, [idx, api]);

  // Navigate functions
  const navigatePrevious = useCallback(() => {
    if (idx > 0) {
      setIdx(prev => prev - 1);
    }
  }, [idx]);

  const navigateNext = useCallback(() => {
    if (idx < photos.length - 1) {
      setIdx(prev => prev + 1);
    }
  }, [idx, photos.length]);

  // Swipe gesture handler
  const bind = useDrag(
    ({ down, movement: [, my], velocity: [, vy] }) => {
      if (!isMobile) return;

      if (down) {
        // User is dragging - hide hint and follow finger
        setShowSwipeHint(false);
        api.start({ y: my, immediate: true });
      } else {
        // User released - decide if we navigate
        const threshold = 80;
        const velocityThreshold = 0.5;

        if (Math.abs(my) > threshold || Math.abs(vy) > velocityThreshold) {
          if (my < 0) {
            // Swiped up = next
            if (idx < photos.length - 1) {
              // Animate out then change index
              api.start({
                y: -window.innerHeight,
                config: { tension: 300, friction: 30 },
                onRest: () => navigateNext()
              });
            } else {
              // At boundary - bounce back
              api.start({ y: 0, config: { tension: 300, friction: 25 } });
            }
          } else {
            // Swiped down = previous
            if (idx > 0) {
              // Animate out then change index
              api.start({
                y: window.innerHeight,
                config: { tension: 300, friction: 30 },
                onRest: () => navigatePrevious()
              });
            } else {
              // At boundary - bounce back
              api.start({ y: 0, config: { tension: 300, friction: 25 } });
            }
          }
        } else {
          // Didn't meet threshold - bounce back
          api.start({ y: 0, config: { tension: 300, friction: 25 } });
        }
      }
    },
    {
      axis: 'y',
      filterTaps: true,
      bounds: { top: -window.innerHeight, bottom: window.innerHeight },
      rubberband: true
    }
  );

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') navigateNext();
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') navigatePrevious();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigatePrevious, navigateNext, onClose]);

  const photo = photos[idx];
  if (!photo) return null;

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.93)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <button
        onClick={onClose}
        style={{ position: 'absolute', top: 20, right: 24, background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', opacity: 0.7 }}
      >
        ✕
      </button>

      {photos.length > 1 && (
        <button
          className="lightbox-arrow-button lightbox-arrow-left"
          onClick={() => setIdx(i => (i - 1 + photos.length) % photos.length)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 52, cursor: 'pointer', padding: 24, lineHeight: 1 }}
        >
          ‹
        </button>
      )}

      <animated.div
        {...bind()}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          y,
          touchAction: isMobile ? 'none' : 'auto',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
        data-testid="lightbox-photo-container"
      >
        <img
          src={photo.url || photo.thumbnail_url}
          alt={photo.title || ''}
          style={{
            maxWidth: isMobile ? '95vw' : '80vw',
            maxHeight: isMobile ? '85vh' : '75vh',
            objectFit: 'contain',
            display: 'block'
          }}
        />
        <div style={{ textAlign: 'center' }}>
          {photo.title && (
            <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, fontFamily: 'var(--font-body)' }}>
              {photo.title}
            </span>
          )}
          {photo.category && (
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, fontFamily: 'var(--font-body)', marginLeft: 8 }}>
              — {photo.category.name}
            </span>
          )}
        </div>
      </animated.div>

      {photos.length > 1 && (
        <button
          className="lightbox-arrow-button lightbox-arrow-right"
          onClick={() => setIdx(i => (i + 1) % photos.length)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 52, cursor: 'pointer', padding: 24, lineHeight: 1 }}
        >
          ›
        </button>
      )}

      {/* Swipe indicator for mobile */}
      {isMobile && showSwipeHint && photos.length > 1 && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          opacity: 0.6,
          animation: 'swipeHintFade 3s ease-out forwards',
          pointerEvents: 'none'
        }}>
          <div style={{
            fontSize: 32,
            color: 'rgba(255,255,255,0.8)',
            animation: 'swipeHintBounce 1.5s ease-in-out infinite'
          }}>
            ↕
          </div>
          <div style={{
            fontSize: 12,
            color: 'rgba(255,255,255,0.8)',
            fontFamily: 'var(--font-body)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            Swipe up/down
          </div>
        </div>
      )}

      <div style={{ position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', color: 'rgba(255,255,255,0.4)', fontSize: 12, fontFamily: 'var(--font-body)' }}>
        {idx + 1} / {photos.length}
      </div>

      {/* Swipe hint animations */}
      <style>{`
        @keyframes swipeHintBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes swipeHintFade {
          0% { opacity: 0.6; }
          80% { opacity: 0.6; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}